import { useEffect, useState, useLayoutEffect, useRef, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { CTA, trackAttrs } from '../utils/track'
import { TbStar } from 'react-icons/tb'
import { FaQuoteLeft } from 'react-icons/fa'
import ContentRenderer from '../components/content/ContentRenderer/ContentRenderer'
import InlineHighlight from '../components/content/InlineHighlight/InlineHighlight'
import HeroArtGrid from '../components/sections/HeroArtGrid/HeroArtGrid'
import { HERO_ART } from '../config/heroArt'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'

interface TestimonialCardProps {
  testimonial: any
  index: number
}

function TestimonialCard({ testimonial }: TestimonialCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showExpandButton, setShowExpandButton] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (contentRef.current) {
      const element = contentRef.current
      setShowExpandButton(element.scrollHeight > 150)
    }
  }, [testimonial.content_md])

  return (
    <div className="group flex flex-col bg-white border-2 border-gray-200 rounded-smooth-lg p-6 lg:p-8 shadow-[0_12px_28px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(0,0,0,0.12)] hover:border-gray-300">
      <div className="mb-4">
        <FaQuoteLeft className="text-2xl text-gray-900/20" aria-hidden="true" />
      </div>

      <div className="flex-1 mb-6 relative">
        {testimonial.content_md && (
          <>
            <div
              ref={contentRef}
              className={`font-body text-[15px] leading-[1.7] text-gray-900/85 transition-all duration-300 ${
                !isExpanded && showExpandButton ? 'max-h-[150px] overflow-hidden' : ''
              }`}
            >
              <ContentRenderer content_md={testimonial.content_md} maxSize="full" />
            </div>
            {!isExpanded && showExpandButton && (
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none" />
            )}
            {showExpandButton && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-3 text-sm font-heading font-bold uppercase tracking-[0.1em] text-gray-900 hover:text-gray-900/70 transition-colors flex items-center gap-1"
              >
                {isExpanded ? (
                  <>
                    Show Less
                    <svg className="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </>
                ) : (
                  <>
                    Read More
                    <svg className="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-between pt-6 border-t border-gray-900/10">
        <div className="flex items-center gap-4">
          {testimonial.image?.url && (
            <div className="flex-shrink-0">
              <img
                src={testimonial.image.url}
                alt={testimonial.name || 'Testimonial author'}
                className="rounded-full border-2 border-gray-200 w-14 h-14 object-cover"
                loading="lazy"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-heading text-sm font-bold uppercase tracking-[0.18em] text-gray-900 mb-1">
              {testimonial.name || 'Anonymous'}
            </h4>
            {testimonial.title && (
              <p className="text-xs font-body text-gray-900/60 leading-tight">{testimonial.title}</p>
            )}
          </div>
        </div>
        {/* No stars here. Nobody ever rated these people's experience: the Review
            table on prod holds zero rows. Five filled stars on every card is what
            made the "5.0 Average Rating" figure above read as though something
            had been measured. The quote is the evidence; a decoration that
            implies a score it does not have takes credibility rather than adding
            it. */}
      </div>
    </div>
  )
}

export default function TestimonialsPage({ items: propItems = [] }: { items?: any[] }) {
  const testimonials = useMemo(() => {
    const items = propItems
    return items.map((item: any) => ({
      ...item,
      createdAt: item.createdAt || item.created_at,
      updatedAt: item.updatedAt || item.updated_at,
      publishedAt: item.publishedAt || item.published_at,
      content_md: item.content_md || '',
    }))
  }, [])

  useLayoutEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      const offset = headerH + navH
      document.documentElement.style.setProperty('--header-nav-height', `${offset}px`)
    }
    measure()
    requestAnimationFrame(measure)
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const totalCount = testimonials.length

  return (
    <div className="min-h-screen bg-gray-50 text-[#232323]">

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
        <ContentColumn anchor="testimonials-hero" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="max-w-3xl">
            <span className="mb-8 inline-flex w-fit items-center gap-2 font-body text-[15px] text-gray-50/80 leading-[1.58] tracking-[-0.003em]">
              <TbStar className="text-base" aria-hidden="true" />
              From people I've worked with
            </span>
            <h1 className="font-sans text-[42px] sm:text-[48px] md:text-[56px] font-bold text-gray-50 mb-6 leading-[1.3] tracking-[-0.02em]">
              In their <InlineHighlight color="red">own words</InlineHighlight>
            </h1>
            <p className="text-[21px] leading-[1.58] text-gray-50/85 font-body mb-12 tracking-[-0.003em]">
              I don't write marketing copy about my mentorship. Here's what the engineers I've worked with actually say.
            </p>
            {/* One number, and it is the one the page is made of: the rows it just
                rendered. No plus sign, because "29+" claims an unknown quantity of
                testimonials that do not exist, and the exact figure is stronger
                than a padded one on a page whose whole argument is that it is not
                marketing copy. */}
            {totalCount > 0 && (
              <div className="flex items-center gap-2">
                <div className="font-heading text-3xl font-bold text-gray-50">{totalCount}</div>
                <div className="font-body text-sm text-gray-50/80">
                  {totalCount === 1 ? 'Testimonial' : 'Testimonials'}
                </div>
              </div>
            )}
          </div>
          <HeroArtGrid images={HERO_ART.testimonials} />
          </div>
        </ContentColumn>
      </section>

      {/* Breadcrumb */}
      <section className="border-b border-border-subtle bg-gray-50">
        <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-4">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.18em] text-gray-900/60 sm:text-sm">
              <li>
                <Link to="/" className="transition-colors hover:text-gray-900 focus:text-gray-900 focus:outline-none">
                  Home
                </Link>
              </li>
              <li className="font-heading text-gray-900">/</li>
              <li className="font-heading text-gray-900" aria-current="page">
                Testimonials
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Stats Section
          This was a four-up band: testimonials, "12K+ Engineers Helped", a "5.0
          Average Rating" and "100% Satisfaction". Three of the four counted
          something nobody counts. There is no register of engineers helped, no
          satisfaction survey, and no rating system at all (the Review table on
          prod holds zero rows, read 2026-08-29), so none of the three could be
          produced if a reader asked where it came from. They sat inches above
          prices from $49 to $299, which is where an unprovable number costs the
          most.
          What is left is the only figure this page can produce on demand, because
          it is a count of what the page itself is about to render. A single true
          statistic reads as a statistic; four, three of them invented, read as
          decoration. */}
      {totalCount > 0 && (
        <section className="border-b border-gray-200 bg-white py-12 md:py-16">
          <ContentColumn anchor="testimonials-stats" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="mb-2 font-heading text-4xl font-black text-gray-900 md:text-5xl">{totalCount}</div>
              <div className="font-body text-sm uppercase tracking-[0.1em] text-gray-900/60">
                {totalCount === 1 ? 'Testimonial' : 'Testimonials'}
              </div>
            </div>
          </ContentColumn>
        </section>
      )}

      {/* Featured Testimonials */}
      {testimonials.length >= 3 && (
        <section className="border-b border-gray-200 bg-brand-cream py-16 md:py-20">
          <ContentColumn anchor="testimonials-featured" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-smooth border-2 border-gray-200 bg-white px-4 py-2">
                <TbStar className="text-base text-gray-900" />
                <span className="font-heading text-xs uppercase tracking-[0.2em] text-gray-900">Featured Stories</span>
              </div>
              <h2 className="mb-4 font-heading text-3xl font-bold uppercase text-gray-900 md:text-4xl">
                <InlineHighlight color="navy">Featured</InlineHighlight> stories
              </h2>
              <p className="mx-auto max-w-2xl font-body text-base text-ink-soft md:text-lg">
                Engineers who made real moves after we worked together
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
              {testimonials.slice(0, 3).map((testimonial: any, index: number) => (
                <div key={`featured-${testimonial.id}`} className="relative">
                  <div className="absolute -right-3 -top-3 z-10">
                    <div className="rounded-smooth bg-gray-900 px-3 py-1.5 font-heading text-xs font-bold uppercase tracking-[0.15em] text-white shadow-lg">
                      Featured
                    </div>
                  </div>
                  <TestimonialCard testimonial={testimonial} index={index} />
                </div>
              ))}
            </div>
          </ContentColumn>
        </section>
      )}

      {/* All Testimonials Grid */}
      <section className="bg-white py-16 md:py-24">
        <ContentColumn anchor="testimonials-list" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 font-heading text-3xl font-bold uppercase text-gray-900 md:text-4xl">
              All <InlineHighlight color="sky">Testimonials</InlineHighlight>
            </h2>
            <p className="font-body text-base text-gray-900/60">Showing all {testimonials.length} testimonials</p>
          </div>

          {testimonials.length === 0 ? (
            <div className="py-20 text-center">
              <p className="font-body text-lg text-gray-900/60">No testimonials available at this time.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {testimonials.map((testimonial: any, index: number) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} index={index} />
              ))}
            </div>
          )}
        </ContentColumn>
      </section>

      {/* CTA Section */}
      <section className="border-t border-gray-900/10 bg-brand-navy py-16 md:py-20">
        <ContentColumn anchor="testimonials-cta" className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-6 font-heading text-3xl font-bold uppercase text-white md:text-4xl">
            Ready to <InlineHighlight color="red">get started</InlineHighlight>?
          </h2>
          <p className="mx-auto mb-8 max-w-2xl font-body text-lg leading-[1.7] text-white/85">
            If you're serious about leveling up, let's talk. No sales pitch -- just an honest conversation about where you are and where you want to go.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              to="/mentorship"
              {...trackAttrs(CTA.mentorship, 'testimonials_page')}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-cream px-8 py-4 font-heading text-[17px] font-bold tracking-[-0.02em] text-gray-900 transition-colors hover:bg-brand-cream/95"
            >
              Get Started with Mentorship
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <Link
              to="/learnings"
              className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-gray-50/40 bg-transparent px-8 py-4 font-heading text-[17px] font-bold tracking-[-0.02em] text-white transition-colors hover:border-gray-50/60 hover:bg-brand-cream/10"
            >
              Explore Courses
            </Link>
          </div>
        </ContentColumn>
      </section>
    </div>
  )
}
